/**
 * Номер отчётной недели, каким его видит заказчик, — общий для клиента и сервера.
 *
 * Верфь и РС нумеруют недели по календарю (ISO-8601, неделя года), а не с начала
 * проекта: присланная Максимом папка «29_QSJY6006_ЕЖЕНЕДЕЛЬНЫЙ_ОТЧЁТ_от_19.07» —
 * это 13–19.07.2026, 29-я неделя 2026 года. У нас в БД `number` — сквозной
 * порядковый номер внутри проекта, и он с календарным не совпадает: та же неделя
 * у корпуса QSJY6006 значилась первой, а у QSJY6008 первой была уже следующая.
 * То есть на вопрос «что делали на 29-й неделе» система давала два разных ответа
 * для двух корпусов одной серии.
 *
 * Поле `number` при этом НЕ трогаем: на нём висит уникальный индекс
 * (project_id, number), а ISO-номера повторяются каждый год — в январе новая
 * неделя столкнулась бы с прошлогодней. `number` остаётся внутренним порядковым
 * (идентичность, сортировка), а наружу — во все подписи, имена папок и отчёты —
 * идёт вычисляемый календарный номер. Хранить его незачем: это чистая функция
 * от date_from.
 *
 * Модуль общий (common), потому что номер показывают обе стороны: сервер — в
 * именах папок Диска и выгрузках, клиент — в селекторе недели, карточках
 * проектов, актах и отчётах.
 */

const MS_IN_WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Номер недели года по ISO-8601 для календарной даты 'YYYY-MM-DD' (1…53).
 *
 * Алгоритм якорится на ЧЕТВЕРГ: по ISO неделя принадлежит тому году, на который
 * приходится её четверг. Поэтому 29–31 декабря могут дать 1-ю неделю следующего
 * года, а 1–3 января — 52/53-ю предыдущего; это не ошибка, а сам стандарт.
 *
 * Считаем через UTC: дата календарная, привязки к часовому поясу у неё нет, и
 * локальная полночь у границы суток сдвигала бы результат (тот же приём, что в
 * weekdayShortRu).
 */
export function isoWeekOfYear(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  // Пн=0 … Вс=6 (getUTCDay даёт Вс=0), затем шаг на четверг своей недели.
  const weekdayFromMonday = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - weekdayFromMonday + 3);

  // 4 января по ISO всегда лежит в 1-й неделе года — от его четверга и считаем.
  const jan4 = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const jan4FromMonday = (jan4.getUTCDay() + 6) % 7;
  jan4.setUTCDate(jan4.getUTCDate() - jan4FromMonday + 3);

  return 1 + Math.round((date.getTime() - jan4.getTime()) / MS_IN_WEEK);
}

/**
 * Понедельник ISO-недели календарной даты 'YYYY-MM-DD' — ключ «одной отчётной
 * недели». Недели в системе строго пн→вс (ensureReportingWeek), поэтому две даты
 * относятся к одной неделе тогда и только тогда, когда их понедельники совпали.
 *
 * Общий для клиента и сервера: сервер валидирует группировку инспекций, клиент
 * той же меркой предупреждает до отправки.
 */
export function isoWeekStart(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekdayFromMonday = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - weekdayFromMonday);
  return date.toISOString().slice(0, 10);
}
